import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import ExclamationMark from '@/assets/icons/exclamation-mark-circle';
import { useAlerts } from '@/feature/alerts/hooks/use-alerts';
import { useTermsAndConditionsModal } from '@/feature/terms-and-conditions/components/TermsAndConditionModalProvider';
import { useHasUserAcceptedProductTerms } from '@/feature/terms-and-conditions/hooks/use-has-user-accepted-product-terms';
import { logger } from '@/utils/logging';

const RIPS_TERMS_LOG_TAG = '[Rips Terms]';

/**
 * Resolves to `true` when the user has accepted the Rips terms, showing the T&C modal first if needed.
 * Resolves to `false` when the modal is dismissed or accepting the terms fails.
 */
export const useRipsTermsCheck = () => {
    const { t } = useTranslation('common');
    const { showInfoSheet } = useAlerts();
    const { showTermsAndConditionsModal } = useTermsAndConditionsModal();
    const { isAccepted: hasAcceptedProductTerms } = useHasUserAcceptedProductTerms();

    return useCallback(
        () =>
            new Promise<boolean>(resolve => {
                if (hasAcceptedProductTerms) {
                    resolve(true);
                    return;
                }

                showTermsAndConditionsModal({
                    onSuccess: () => resolve(true),
                    onCancel: () => resolve(false),
                    onTermsError: error => {
                        logger.error(RIPS_TERMS_LOG_TAG, 'Error accepting Rips user terms and conditions', error);
                        resolve(false);
                        showInfoSheet({
                            title: t('simple_error_message'),
                            description: t('error_subtitle'),
                            secondaryLabel: t('got_it'),
                            icon: <ExclamationMark />,
                        });
                    },
                });
            }),
        [hasAcceptedProductTerms, showTermsAndConditionsModal, showInfoSheet, t]
    );
};
